import React, { useEffect, useState } from "react";
import api from "../../api";
import { Pagination, Stack } from "@mui/material";
import { useAlert } from "react-alert";

export default function OnboardingAdmin() {
  const alert = useAlert();
  const [settings, setSettings] = useState(null);
  const [weeksInput, setWeeksInput] = useState(4);
  const [cooldownInput, setCooldownInput] = useState(1440);
  const [savingSettings, setSavingSettings] = useState(false);

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const loadSettings = async () => {
    try {
      const resp = await api.getOnboardingAdminSettings();
      setSettings(resp.data?.settings);
      setWeeksInput(resp.data?.settings?.demoTradingWeeks ?? 4);
      setCooldownInput(resp.data?.settings?.submissionCooldownMinutes ?? 1440);
    } catch (_) {}
  };

  const loadUsers = async (p = page) => {
    setLoading(true);
    try {
      const resp = await api.getOnboardingAdminUsers({ page: p, limit });
      setUsers(resp.data?.rows || []);
      setTotal(resp.data?.total || 0);
    } catch (_) {
      alert.show("Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadUsers(1);
  }, []);

  const handleSaveSettings = async () => {
    if (!weeksInput || weeksInput < 1 || weeksInput > 52) {
      alert.show("Weeks must be between 1 and 52.");
      return;
    }
    setSavingSettings(true);
    try {
      await api.updateOnboardingAdminSettings({ demoTradingWeeks: Number(weeksInput), submissionCooldownMinutes: Number(cooldownInput) });
      alert.show("Settings saved.");
      loadSettings();
    } catch (_) {
      alert.show("Failed to save settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const handlePageChange = (_, val) => {
    setPage(val);
    loadUsers(val);
  };

  const badge = (val, trueLabel = "Yes", falseLabel = "No", trueColor = "#059669", falseColor = "#9a94ad") => (
    <span style={{
      fontFamily: "monospace", fontSize: 10.5, fontWeight: 800, letterSpacing: ".08em",
      borderRadius: 6, padding: "3px 8px",
      color: val ? trueColor : falseColor,
      background: val ? "rgba(5,150,105,.1)" : "#f5f5f8",
    }}>
      {val ? trueLabel : falseLabel}
    </span>
  );

  return (
    <div style={{ padding: "28px 24px", fontFamily: "'Segoe UI', sans-serif", color: "#141020", maxWidth: 1200 }}>
      <h2 style={{ margin: "0 0 24px", fontSize: 22, fontWeight: 800 }}>Onboarding Course Settings</h2>

      {/* Settings card */}
      <div style={{ background: "#fff", border: "1px solid #eeebf6", borderRadius: 16, padding: "24px 28px", marginBottom: 32, maxWidth: 480 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Demo Trading Duration</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <label style={{ fontSize: 13.5, color: "#6a6580", minWidth: 120 }}>
            Weeks required:
          </label>
          <input
            type="number" min={1} max={52}
            value={weeksInput}
            onChange={(e) => setWeeksInput(e.target.value)}
            style={{ width: 80, padding: "8px 12px", border: "1px solid #e0d5fb", borderRadius: 10, fontSize: 15, fontWeight: 700, textAlign: "center", outline: "none" }}
          />
          <span style={{ fontSize: 13, color: "#9a94ad" }}>× 7 = {weeksInput * 7} days</span>
        </div>
        <div style={{ fontSize: 12, color: "#a9a4bd", marginTop: 8 }}>
          Default: 4 weeks (28 days). Current: <strong>{settings?.demoTradingWeeks ?? "—"} weeks ({(settings?.demoTradingWeeks ?? 0) * 7} days)</strong>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 18 }}>
          <label style={{ fontSize: 13.5, color: "#6a6580", minWidth: 120 }}>
            Cooldown (mins):
          </label>
          <input
            type="number" min={1}
            value={cooldownInput}
            onChange={(e) => setCooldownInput(e.target.value)}
            style={{ width: 90, padding: "8px 12px", border: "1px solid #e0d5fb", borderRadius: 10, fontSize: 15, fontWeight: 700, textAlign: "center", outline: "none" }}
          />
          <span style={{ fontSize: 13, color: "#9a94ad" }}>
            {cooldownInput >= 1440
              ? `${(cooldownInput / 1440).toFixed(1)} day(s)`
              : cooldownInput >= 60
                ? `${(cooldownInput / 60).toFixed(1)} hour(s)`
                : `${cooldownInput} min(s)`}
          </span>
        </div>
        <div style={{ fontSize: 12, color: "#a9a4bd", marginTop: 6 }}>
          Default: 1440 (24h). Set to <strong>1</strong> for instant testing. Current: <strong>{settings?.submissionCooldownMinutes ?? "—"} min(s)</strong>
        </div>
        <button
          disabled={savingSettings}
          onClick={handleSaveSettings}
          style={{
            marginTop: 18, padding: "10px 26px", borderRadius: 10, border: "none", cursor: "pointer",
            fontWeight: 800, fontSize: 13.5, letterSpacing: ".04em",
            background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "#fff",
            opacity: savingSettings ? 0.7 : 1,
          }}
        >
          {savingSettings ? "Saving…" : "Save Settings"}
        </button>
      </div>

      {/* Users table */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>User Onboarding Progress</h3>
        <span style={{ fontFamily: "monospace", fontSize: 12, color: "#9a94ad" }}>{total} users</span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
          <thead>
            <tr style={{ background: "#f6f5fb" }}>
              {["Name", "Email", "Beginner Videos", "Advance Videos", "Demo Trades", "Wins", "Losses", "Win Rate", "Unlocked", "Skipped"].map((h) => (
                <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 800, fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase", color: "#7c3aed", borderBottom: "1px solid #eeebf6", whiteSpace: "nowrap" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ textAlign: "center", padding: 40, color: "#9a94ad" }}>Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: "center", padding: 40, color: "#9a94ad" }}>No users found</td></tr>
            ) : users.map((u, i) => (
              <tr key={u.userId} style={{ background: i % 2 === 0 ? "#fff" : "#faf9fe", borderBottom: "1px solid #f0edf8" }}>
                <td style={{ padding: "10px 14px", fontWeight: 600 }}>{u.name}</td>
                <td style={{ padding: "10px 14px", color: "#6a6580", fontFamily: "monospace", fontSize: 12 }}>{u.email}</td>
                <td style={{ padding: "10px 14px", textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}>{u.beginnerVideosWatched}</td>
                <td style={{ padding: "10px 14px", textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}>{u.advanceVideosWatched}</td>
                <td style={{ padding: "10px 14px", textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}>{u.demoTradesTaken}</td>
                <td style={{ padding: "10px 14px", textAlign: "center", fontFamily: "monospace", fontWeight: 700, color: "#059669" }}>{u.demoWins}</td>
                <td style={{ padding: "10px 14px", textAlign: "center", fontFamily: "monospace", fontWeight: 700, color: "#dc2626" }}>{u.demoLosses}</td>
                <td style={{ padding: "10px 14px", textAlign: "center", fontFamily: "monospace", fontWeight: 800, color: u.demoWinRate >= 62 ? "#059669" : "#6a6580" }}>
                  {u.demoTradesTaken > 0 ? `${u.demoWinRate}%` : "—"}
                </td>
                <td style={{ padding: "10px 14px", textAlign: "center" }}>{badge(u.demoTradingUnlocked, "Unlocked", "Locked", "#059669", "#a9a4bd")}</td>
                <td style={{ padding: "10px 14px", textAlign: "center" }}>{badge(u.continueWithoutCourse, "Skipped", "No", "#b45309", "#a9a4bd")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > limit && (
        <Stack alignItems="center" style={{ marginTop: 24 }}>
          <Pagination
            count={Math.ceil(total / limit)}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Stack>
      )}
    </div>
  );
}
